import { ENodeKind, ENodeType, ERROR_PORT, WAIT_MAIN_PORT } from '../constants/flow.constants';
import {
  nodeTypeOf,
  onErrorEdgeOutput,
  type INodeOutputSpec,
  type INodeTypeDescriptor,
} from '../registry/node-types.registry';
import type { TFlowEdge, TFlowGraph, TFlowNode } from '../schemas/flow-graph.schema';

/** Scope of the nodes outside every loop; a loop's body is the scope named by the loop key. */
export const ROOT_SCOPE = '';

// A draft may be half-broken: unknown nodes and crossing edges are skipped here, never thrown —
// reporting them is the validator's job.
export interface IGraphIndex {
  graph: TFlowGraph;
  nodes: Map<string, TFlowNode>;
  descriptors: Map<string, INodeTypeDescriptor | undefined>;
  outputs: Map<string, INodeOutputSpec[]>;
  inputs: Map<string, string[]>;
  parentLoop: Map<string, string>;
  scopeMembers: Map<string, string[]>;
  out: Map<string, TFlowEdge[]>;
  in: Map<string, TFlowEdge[]>;
  trigger: string | null;
}

export function buildGraphIndex(graph: TFlowGraph): IGraphIndex {
  const nodes = new Map<string, TFlowNode>();
  for (const node of graph.nodes) if (!nodes.has(node.key)) nodes.set(node.key, node);

  const descriptors = new Map<string, INodeTypeDescriptor | undefined>();
  const outputs = new Map<string, INodeOutputSpec[]>();
  const inputs = new Map<string, string[]>();
  for (const [key, node] of nodes) {
    const descriptor = nodeTypeOf(node.type);
    descriptors.set(key, descriptor);
    outputs.set(key, descriptor ? descriptor.outputs(node.config ?? {}) : []);
    inputs.set(key, descriptor ? descriptor.inputs(node.config ?? {}) : []);
  }

  const parentLoop = new Map<string, string>();
  for (const [key, node] of nodes) {
    if (node.type !== ENodeType.LOOP) continue;
    for (const child of node.children ?? []) {
      if (child !== key && nodes.has(child) && !parentLoop.has(child)) parentLoop.set(child, key);
    }
  }
  // A containment cycle (A holds B holds A) is cut here; the validator names it.
  for (const key of [...parentLoop.keys()]) {
    const seen = new Set<string>([key]);
    let cursor = parentLoop.get(key);
    while (cursor !== undefined) {
      if (seen.has(cursor)) {
        parentLoop.delete(key);
        break;
      }
      seen.add(cursor);
      cursor = parentLoop.get(cursor);
    }
  }

  const scopeMembers = new Map<string, string[]>([[ROOT_SCOPE, []]]);
  for (const key of nodes.keys()) {
    const scope = parentLoop.get(key) ?? ROOT_SCOPE;
    if (!scopeMembers.has(scope)) scopeMembers.set(scope, []);
    scopeMembers.get(scope)!.push(key);
  }
  for (const [key, node] of nodes) {
    if (node.type === ENodeType.LOOP && !scopeMembers.has(key)) scopeMembers.set(key, []);
  }

  const out = new Map<string, TFlowEdge[]>();
  const into = new Map<string, TFlowEdge[]>();
  for (const key of nodes.keys()) {
    out.set(key, []);
    into.set(key, []);
  }
  const seenEdges = new Set<string>();
  for (const rawEdge of graph.edges) {
    if (!nodes.has(rawEdge.from) || !nodes.has(rawEdge.to) || rawEdge.from === rawEdge.to) continue;
    if (
      (parentLoop.get(rawEdge.from) ?? ROOT_SCOPE) !== (parentLoop.get(rawEdge.to) ?? ROOT_SCOPE)
    ) {
      continue;
    }
    // Legacy alias (item 2): the migration rewrites stored graphs, this keeps any that
    // slipped through resolvable too.
    const normalized = legacyEdgeOutput(nodes.get(rawEdge.from), rawEdge.output);
    const edge = normalized === rawEdge.output ? rawEdge : { ...rawEdge, output: normalized };
    const id = edgeId(edge);
    if (seenEdges.has(id)) continue;
    seenEdges.add(id);
    out.get(edge.from)!.push(edge);
    into.get(edge.to)!.push(edge);
  }

  const trigger =
    scopeMembers.get(ROOT_SCOPE)!.find((key) => descriptors.get(key)?.kind === ENodeKind.TRIGGER) ??
    null;

  return {
    graph,
    nodes,
    descriptors,
    outputs,
    inputs,
    parentLoop,
    scopeMembers,
    out,
    in: into,
    trigger,
  };
}

/**
 * Item 2 legacy alias: an edge drawn before the wait-port rename still reads "main". And an
 * `ok` edge of a step switched to "continue on fail" reads `done`, or back (§3.4).
 */
export function legacyEdgeOutput(node: TFlowNode | undefined, output: string): string {
  if (!node) return output;
  if (node.type === ENodeType.WAIT && output === 'main') return WAIT_MAIN_PORT;
  return onErrorEdgeOutput(node, output);
}

/** The draft as saved: `ok`/`done` edges renamed to what their step's setting reads (§3.4). */
export function normalizeOnErrorEdges(graph: TFlowGraph): TFlowGraph {
  const nodes = new Map(graph.nodes.map((node) => [node.key, node]));
  const seen = new Set<string>();
  let changed = false;
  const edges: TFlowEdge[] = [];
  for (const edge of graph.edges) {
    const from = nodes.get(edge.from);
    const output = from ? onErrorEdgeOutput(from, edge.output) : edge.output;
    const next = output === edge.output ? edge : { ...edge, output };
    if (next !== edge) changed = true;
    const id = edgeId(next);
    if (next !== edge && seen.has(id)) continue;
    seen.add(id);
    edges.push(next);
  }
  return changed ? { ...graph, edges } : graph;
}

export function edgeId(edge: Pick<TFlowEdge, 'from' | 'output' | 'to' | 'input'>): string {
  return `${edge.from}\u0000${edge.output}\u0000${edge.to}\u0000${edge.input}`;
}

export function scopeOf(index: IGraphIndex, key: string): string {
  return index.parentLoop.get(key) ?? ROOT_SCOPE;
}

export function enclosingLoops(index: IGraphIndex, key: string): string[] {
  const loops: string[] = [];
  let cursor = index.parentLoop.get(key);
  while (cursor !== undefined) {
    loops.push(cursor);
    cursor = index.parentLoop.get(cursor);
  }
  return loops;
}

export function successors(index: IGraphIndex, key: string): string[] {
  return unique((index.out.get(key) ?? []).map((edge) => edge.to));
}

function predecessors(index: IGraphIndex, key: string): string[] {
  return unique((index.in.get(key) ?? []).map((edge) => edge.from));
}

interface ITopoResult {
  order: string[];
  blocked: string[];
}

export function topoOrder(index: IGraphIndex, scope: string): ITopoResult {
  const members = index.scopeMembers.get(scope) ?? [];
  const indegree = new Map(members.map((key) => [key, predecessors(index, key).length]));
  const queue = members.filter((key) => indegree.get(key) === 0);
  const order: string[] = [];
  while (queue.length > 0) {
    const key = queue.shift()!;
    order.push(key);
    for (const next of successors(index, key)) {
      const left = indegree.get(next)! - 1;
      indegree.set(next, left);
      if (left === 0) queue.push(next);
    }
  }
  const placed = new Set(order);
  return { order, blocked: members.filter((key) => !placed.has(key)) };
}

export function cycleEdges(index: IGraphIndex): TFlowEdge[] {
  const state = new Map<string, 'open' | 'done'>();
  const found: TFlowEdge[] = [];
  const visit = (key: string): void => {
    state.set(key, 'open');
    for (const edge of index.out.get(key) ?? []) {
      const next = state.get(edge.to);
      if (next === 'open') found.push(edge);
      else if (next === undefined) visit(edge.to);
    }
    state.set(key, 'done');
  };
  for (const key of index.nodes.keys()) if (!state.has(key)) visit(key);
  return found;
}

// Publishing a 200-node graph asks these tens of thousands of times; the index never changes.
const closures = new WeakMap<IGraphIndex, Map<string, ReadonlySet<string>>>();

function closure(index: IGraphIndex, key: string, direction: 'down' | 'up'): ReadonlySet<string> {
  let cache = closures.get(index);
  if (!cache) closures.set(index, (cache = new Map()));
  const id = `${direction}\u0000${key}`;
  const cached = cache.get(id);
  if (cached) return cached;
  const step = direction === 'down' ? successors : predecessors;
  const seen = new Set<string>();
  const stack = step(index, key);
  while (stack.length > 0) {
    const next = stack.pop()!;
    if (seen.has(next)) continue;
    seen.add(next);
    stack.push(...step(index, next));
  }
  cache.set(id, seen);
  return seen;
}

export function descendants(index: IGraphIndex, key: string): ReadonlySet<string> {
  return closure(index, key, 'down');
}

export function ancestors(index: IGraphIndex, key: string): ReadonlySet<string> {
  return closure(index, key, 'up');
}

export function scopeEntries(index: IGraphIndex, scope: string): string[] {
  if (scope === ROOT_SCOPE) return index.trigger ? [index.trigger] : [];
  return (index.scopeMembers.get(scope) ?? []).filter(
    (key) => predecessors(index, key).length === 0,
  );
}

export function scopeExits(index: IGraphIndex, scope: string): string[] {
  return (index.scopeMembers.get(scope) ?? []).filter((key) => successors(index, key).length === 0);
}

// A cyclic scope yields dominators for its acyclic part only, which is enough for a draft.
function scopeDominators(index: IGraphIndex, scope: string): Map<string, Set<string>> {
  const entries = scopeEntries(index, scope);
  const dominators = new Map<string, Set<string>>();
  if (entries.length !== 1) return dominators;
  const entry = entries[0];
  const reachable = new Set([entry, ...descendants(index, entry)]);
  for (const key of topoOrder(index, scope).order) {
    if (!reachable.has(key)) continue;
    if (key === entry) {
      dominators.set(key, new Set([key]));
      continue;
    }
    const sets = predecessors(index, key)
      .map((pred) => dominators.get(pred))
      .filter((set): set is Set<string> => set !== undefined);
    const own = new Set(
      sets.length === 0 ? [] : [...sets[0]].filter((k) => sets.every((set) => set.has(k))),
    );
    own.add(key);
    dominators.set(key, own);
  }
  return dominators;
}

export interface IDominance {
  local: Map<string, Set<string>>;
  /** What a node may read (§5.3): dominators, plus what a waiting Merge guarantees. */
  visible(key: string): Set<string>;
  chain(key: string): string[];
}

const dominances = new WeakMap<IGraphIndex, IDominance>();

export function buildDominance(index: IGraphIndex): IDominance {
  const known = dominances.get(index);
  if (known) return known;
  const local = new Map<string, Set<string>>();
  const readable = new Map<string, Set<string>>();
  for (const scope of index.scopeMembers.keys()) {
    const dominators = scopeDominators(index, scope);
    for (const [key, set] of dominators) local.set(key, set);
    for (const [key, set] of readableSets(index, scope, dominators)) readable.set(key, set);
  }
  const lift = (own: (key: string) => Set<string> | undefined) => {
    const cache = new Map<string, Set<string>>();
    const resolve = (key: string): Set<string> => {
      const cached = cache.get(key);
      if (cached) return cached;
      const set = new Set([...(own(key) ?? [])].filter((k) => k !== key));
      const loop = index.parentLoop.get(key);
      if (loop !== undefined) for (const k of resolve(loop)) set.add(k);
      cache.set(key, set);
      return set;
    };
    return resolve;
  };
  const strict = lift((key) => local.get(key));
  const visible = lift((key) => readable.get(key) ?? local.get(key));
  // Dominators of one node form a chain, and each sees strictly more than the one above it.
  const chain = (key: string): string[] =>
    [...strict(key)].sort((a, b) => strict(b).size - strict(a).size);
  const dominance = { local, visible, chain };
  dominances.set(index, dominance);
  return dominance;
}

const WAITING_MERGE_MODES: readonly string[] = ['all', 'combine', 'collect'];

export function mergeWaitsForAll(node: TFlowNode | undefined): boolean {
  return (
    node?.type === ENodeType.MERGE &&
    WAITING_MERGE_MODES.includes(String(node.config?.mode ?? 'all'))
  );
}

// As dominators, except a waiting Merge's main output also carries its certain inputs (§4.5).
function readableSets(
  index: IGraphIndex,
  scope: string,
  dominators: Map<string, Set<string>>,
): Map<string, Set<string>> {
  const base = new Map<string, Set<string>>();
  const full = new Map<string, Set<string>>();
  const entries = scopeEntries(index, scope);
  if (entries.length !== 1) return full;
  const reachable = new Set([entries[0], ...descendants(index, entries[0])]);
  const out = (edge: TFlowEdge): Set<string> | undefined =>
    edge.output === ERROR_PORT ? base.get(edge.from) : full.get(edge.from);
  const meet = (edges: readonly TFlowEdge[]): Set<string> => {
    const sets = edges.map(out).filter((set): set is Set<string> => set !== undefined);
    if (sets.length === 0) return new Set();
    return new Set([...sets[0]].filter((k) => sets.every((set) => set.has(k))));
  };
  for (const key of topoOrder(index, scope).order) {
    if (!reachable.has(key)) continue;
    const incoming = (index.in.get(key) ?? []).filter((edge) => reachable.has(edge.from));
    const own = key === entries[0] ? new Set<string>() : meet(incoming);
    own.add(key);
    base.set(key, own);
    if (!mergeWaitsForAll(index.nodes.get(key))) {
      full.set(key, own);
      continue;
    }
    const joined = new Set(own);
    for (const input of certainInputs(index, dominators, key)) {
      for (const k of meet(incoming.filter((edge) => edge.input === input))) joined.add(k);
    }
    full.set(key, joined);
  }
  return full;
}

// From the Merge's nearest dominator every non-error outcome sends a copy on to the input.
export function certainInputs(
  index: IGraphIndex,
  dominators: Map<string, Set<string>>,
  merge: string,
): string[] {
  const above = [...(dominators.get(merge) ?? [])].filter((k) => k !== merge);
  if (above.length === 0) return [];
  const idom = above.reduce((a, b) =>
    (dominators.get(b)?.size ?? 0) > (dominators.get(a)?.size ?? 0) ? b : a,
  );
  return (index.inputs.get(merge) ?? []).filter((input) => {
    const memo = new Map<string, boolean>();
    const leads = (key: string): boolean => {
      const known = memo.get(key);
      if (known !== undefined) return known;
      memo.set(key, false);
      const node = index.nodes.get(key);
      const specs = index.outputs.get(key) ?? [];
      const passes = node?.disabled === true && index.descriptors.get(key)?.disableable === true;
      const ports = specs
        .filter((spec) => spec.key !== ERROR_PORT && (!passes || spec.main))
        .map((spec) => spec.key);
      const edges = index.out.get(key) ?? [];
      const result =
        ports.length > 0 &&
        ports.every((port) =>
          edges.some(
            (edge) =>
              edge.output === port && (edge.to === merge ? edge.input === input : leads(edge.to)),
          ),
        );
      memo.set(key, result);
      return result;
    };
    return leads(idom);
  });
}

export function joinInputReach(index: IGraphIndex, join: string): Map<string, Set<string>> {
  const reach = new Map<string, Set<string>>();
  for (const input of index.inputs.get(join) ?? []) {
    const set = new Set<string>();
    for (const edge of index.in.get(join) ?? []) {
      if (edge.input !== input) continue;
      set.add(edge.from);
      for (const key of ancestors(index, edge.from)) set.add(key);
    }
    reach.set(input, set);
  }
  return reach;
}

export function isJoin(index: IGraphIndex, key: string): boolean {
  return index.descriptors.get(key)?.kind === ENodeKind.JOIN;
}

export function joinReach(index: IGraphIndex): Map<string, string[]> {
  const local = new Map<string, Set<string>>();
  for (const join of index.nodes.keys()) {
    if (!isJoin(index, join)) continue;
    for (const [input, nodes] of joinInputReach(index, join)) {
      for (const from of nodes) {
        if (!local.has(from)) local.set(from, new Set());
        local.get(from)!.add(`${join}:${input}`);
      }
    }
  }
  const lifted = new Map<string, Set<string>>();
  const resolve = (key: string): Set<string> => {
    const cached = lifted.get(key);
    if (cached) return cached;
    const set = new Set(local.get(key) ?? []);
    const loop = index.parentLoop.get(key);
    if (loop !== undefined) for (const pair of resolve(loop)) set.add(pair);
    lifted.set(key, set);
    return set;
  };
  const reach = new Map<string, string[]>();
  for (const key of index.nodes.keys()) {
    const set = resolve(key);
    if (set.size > 0) reach.set(key, [...set].sort());
  }
  return reach;
}

export function edgeFanoutNodes(index: IGraphIndex): Set<string> {
  return new Set(fanoutPorts(index).map((port) => port.node));
}

// Walking up the dominator chain an Aggregate closes the nearest Split not yet closed (§4.5).
export function splitBrackets(
  index: IGraphIndex,
  dominance: IDominance,
  key: string,
): { open: string | null; closed: { split: string; aggregate: string }[] } {
  const pending: string[] = [];
  const closed: { split: string; aggregate: string }[] = [];
  for (const k of dominance.chain(key)) {
    const type = index.nodes.get(k)?.type;
    if (type === ENodeType.AGGREGATE) pending.push(k);
    if (type !== ENodeType.SPLIT) continue;
    const aggregate = pending.pop();
    if (aggregate === undefined) return { open: k, closed };
    closed.push({ split: k, aggregate });
  }
  return { open: null, closed };
}

export function pairedSplit(index: IGraphIndex, dominance: IDominance, aggregate: string) {
  return splitBrackets(index, dominance, aggregate).open;
}

interface IFanoutPort {
  node: string;
  output: string;
  edges: TFlowEdge[];
}

/** Ports with more than one edge: the only places one copy becomes several (§4.3). */
export function fanoutPorts(index: IGraphIndex): IFanoutPort[] {
  const ports: IFanoutPort[] = [];
  for (const [node, edges] of index.out) {
    const byOutput = new Map<string, TFlowEdge[]>();
    for (const edge of edges) {
      if (!byOutput.has(edge.output)) byOutput.set(edge.output, []);
      byOutput.get(edge.output)!.push(edge);
    }
    for (const [output, list] of byOutput) {
      if (list.length > 1) ports.push({ node, output, edges: list });
    }
  }
  return ports;
}

function edgeReachesInput(
  edge: TFlowEdge,
  merge: string,
  input: string,
  reach: Map<string, Set<string>>,
): boolean {
  return (edge.to === merge && edge.input === input) || (reach.get(input)?.has(edge.to) ?? false);
}

// Without a fan-out port sending one copy toward each input, a single copy takes one path only.
export function inputsCanCoOccur(
  ports: IFanoutPort[],
  merge: string,
  a: string,
  b: string,
  reach: Map<string, Set<string>>,
): boolean {
  return ports.some((port) =>
    port.edges.some(
      (e1) =>
        edgeReachesInput(e1, merge, a, reach) &&
        port.edges.some((e2) => e2 !== e1 && edgeReachesInput(e2, merge, b, reach)),
    ),
  );
}

export function countPaths(index: IGraphIndex, from: string, to: string, cap = 1_000): number {
  const memo = new Map<string, number>();
  const count = (key: string, depth: number): number => {
    if (key === to) return 1;
    if (depth > index.nodes.size) return 0;
    const cached = memo.get(key);
    if (cached !== undefined) return cached;
    let total = 0;
    for (const edge of index.out.get(key) ?? []) {
      total = Math.min(cap, total + count(edge.to, depth + 1));
    }
    memo.set(key, total);
    return total;
  };
  return count(from, 0);
}

function unique<T>(items: T[]): T[] {
  return [...new Set(items)];
}
